import { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle,
  DialogDescription 
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { 
  HelpCircle, ChevronRight, ChevronLeft, X, Check,
  Ruler, User, FileText, ClipboardCheck, ArrowRight
} from 'lucide-react';

const TUTORIAL_SECTIONS = [
  {
    id: 'measurements',
    title: 'How to Add Measurements',
    icon: Ruler,
    color: 'from-cyan-500 to-blue-500',
    steps: [
      { title: 'Navigate to Profile', description: 'Click on any athlete card from the dashboard to open their profile page.' },
      { title: 'Find the Measurements Tab', description: 'Look for the "Measurements" tab in the profile navigation.' },
      { title: 'Click Add Measurement', description: 'Click the "Add Measurement" button to open the form.' },
      { title: 'Enter the Data', description: 'Fill in height (cm) and weight (kg). The date defaults to today.' },
      { title: 'Save Your Entry', description: 'Click "Save" to record the measurement. You\'ll see it appear in the history.' }
    ]
  },
  {
    id: 'profile',
    title: 'How to Complete Profile Info',
    icon: User,
    color: 'from-purple-500 to-pink-500',
    steps: [
      { title: 'Access Profile Settings', description: 'From the athlete profile, click the "Edit Profile" button or pencil icon.' },
      { title: 'Add Basic Information', description: 'Enter first name, last name, date of birth, and gender.' },
      { title: 'Set Team Information', description: 'Select the current team from the dropdown or add a new team.' },
      { title: 'Parent Heights (Optional)', description: 'Add mother and father heights for more accurate growth predictions.' },
      { title: 'Contact Information', description: 'Add email and phone number for notifications and reminders.' },
      { title: 'Save Changes', description: 'Click "Save" to update the profile. Changes are reflected immediately.' }
    ]
  },
  {
    id: 'questionnaires',
    title: 'How to Complete Questionnaires',
    icon: FileText,
    color: 'from-orange-500 to-red-500',
    steps: [
      { title: 'Find the Medical Tab', description: 'Navigate to the athlete profile and select the "Medical" tab.' },
      { title: 'Start Medical Questionnaire', description: 'Click "Complete Questionnaire" to begin the medical history form.' },
      { title: 'Answer Health Questions', description: 'Provide information about medical conditions, allergies, and medications.' },
      { title: 'Emergency Contacts', description: 'Add emergency contact details - these sync to the profile automatically.' },
      { title: 'Complete SMHAT Assessment', description: 'If available, complete the mental health screening (SMHAT-1).' },
      { title: 'Submit and Review', description: 'Submit the questionnaire. You can update it anytime from the Medical tab.' }
    ]
  },
  {
    id: 'consents',
    title: 'How to Complete Consent Forms',
    icon: ClipboardCheck,
    color: 'from-green-500 to-emerald-500',
    steps: [
      { title: 'Locate Consent Section', description: 'From the athlete profile, find the "Consent Status" section in the Overview.' },
      { title: 'Check Missing Consents', description: 'Red indicators show which consents are pending. Click to start.' },
      { title: 'Review Terms', description: 'Read through the consent terms carefully before agreeing.' },
      { title: 'Provide Signature', description: 'Enter your name as an electronic signature and check the agreement box.' },
      { title: 'Submit Consent', description: 'Click "Submit" to record your consent. The status will turn green.' },
      { title: 'View Completed', description: 'Completed consents show the date and signatory name for reference.' }
    ]
  }
];

export default function AppTutorial() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [currentStep, setCurrentStep] = useState(0);
  const [completedSections, setCompletedSections] = useState([]);

  // Load completed sections from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('tutorial_completed_sections');
    if (saved) {
      setCompletedSections(JSON.parse(saved));
    }
  }, []);

  // Check if first time user
  useEffect(() => {
    const hasSeenTutorial = localStorage.getItem('tutorial_seen');
    if (!hasSeenTutorial) {
      // Show tutorial automatically for new users after a short delay
      const timer = setTimeout(() => setIsOpen(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const markSectionComplete = (sectionId) => {
    const updated = [...completedSections, sectionId];
    setCompletedSections(updated);
    localStorage.setItem('tutorial_completed_sections', JSON.stringify(updated));
  };

  const handleSectionClick = (section) => {
    setCurrentSection(section);
    setCurrentStep(0);
  };

  const handleNext = () => {
    if (currentStep < currentSection.steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      // Mark section complete
      if (!completedSections.includes(currentSection.id)) {
        markSectionComplete(currentSection.id);
      }
      setCurrentSection(null);
      setCurrentStep(0);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setCurrentSection(null);
    setCurrentStep(0);
    localStorage.setItem('tutorial_seen', 'true');
  };

  const handleSkip = () => {
    handleClose();
  };

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-white shadow-lg hover:shadow-xl hover:scale-105 transition-all flex items-center justify-center group"
        title="App Tutorial"
        data-testid="tutorial-help-btn"
      >
        <HelpCircle className="h-6 w-6 group-hover:scale-110 transition-transform" />
      </button>

      {/* Tutorial Dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-700 text-white max-h-[85vh] overflow-hidden">
          {!currentSection ? (
            // Overview Mode - Show all sections
            <>
              <DialogHeader>
                <DialogTitle className="text-xl font-bold flex items-center gap-2">
                  <HelpCircle className="h-6 w-6 text-indigo-400" />
                  App Tutorial
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  Learn how to use the key features of the app. Click a section to get started.
                </DialogDescription>
              </DialogHeader>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                {TUTORIAL_SECTIONS.map((section) => {
                  const Icon = section.icon;
                  const isComplete = completedSections.includes(section.id);
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => handleSectionClick(section)}
                      className={`p-4 rounded-xl border transition-all text-left group hover:scale-[1.02] ${
                        isComplete 
                          ? 'border-green-500/50 bg-green-500/10' 
                          : 'border-zinc-700 bg-zinc-800/50 hover:border-indigo-500/50'
                      }`}
                      data-testid={`tutorial-section-${section.id}`}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${section.color} flex items-center justify-center shrink-0`}>
                          <Icon className="h-5 w-5 text-white" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <h3 className="font-semibold text-white">{section.title}</h3>
                            {isComplete && (
                              <Check className="h-4 w-4 text-green-400" />
                            )}
                          </div>
                          <p className="text-xs text-zinc-400 mt-1">
                            {section.steps.length} steps
                          </p>
                        </div>
                        <ArrowRight className="h-5 w-5 text-zinc-500 group-hover:text-indigo-400 group-hover:translate-x-1 transition-all" />
                      </div>
                    </button>
                  );
                })}
              </div>
              
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-zinc-700">
                <p className="text-sm text-zinc-500">
                  {completedSections.length}/{TUTORIAL_SECTIONS.length} sections completed
                </p>
                <Button
                  variant="outline"
                  onClick={handleSkip}
                  className="border-zinc-600"
                >
                  Close Tutorial
                </Button>
              </div>
            </>
          ) : (
            // Step-by-Step Mode - Show current section
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <button 
                    onClick={() => setCurrentSection(null)}
                    className="text-zinc-400 hover:text-white flex items-center gap-1 text-sm"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    Back to Overview
                  </button>
                  <button onClick={handleClose} className="text-zinc-500 hover:text-white">
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <DialogTitle className="text-xl font-bold flex items-center gap-2 mt-2">
                  {(() => {
                    const Icon = currentSection.icon;
                    return (
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${currentSection.color} flex items-center justify-center`}>
                        <Icon className="h-4 w-4 text-white" />
                      </div>
                    );
                  })()}
                  {currentSection.title}
                </DialogTitle>
              </DialogHeader>
              
              {/* Progress Dots */}
              <div className="flex items-center justify-center gap-2 mt-4">
                {currentSection.steps.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentStep(index)}
                    className={`w-2.5 h-2.5 rounded-full transition-all ${
                      index === currentStep
                        ? 'bg-indigo-500 w-6'
                        : index < currentStep
                        ? 'bg-green-500'
                        : 'bg-zinc-600'
                    }`}
                  />
                ))}
              </div>
              
              {/* Current Step Content */}
              <div className="mt-6 min-h-[150px]">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-xs font-medium text-indigo-400 bg-indigo-500/20 px-2 py-1 rounded">
                    Step {currentStep + 1} of {currentSection.steps.length}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {currentSection.steps[currentStep].title}
                </h3>
                <p className="text-zinc-300 leading-relaxed">
                  {currentSection.steps[currentStep].description}
                </p>
              </div>
              
              {/* Navigation Buttons */}
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-zinc-700">
                <Button
                  variant="outline"
                  onClick={handlePrevious}
                  disabled={currentStep === 0}
                  className="border-zinc-600 disabled:opacity-50"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <Button
                  onClick={handleNext}
                  className={`bg-gradient-to-r ${currentSection.color} text-white`}
                >
                  {currentStep === currentSection.steps.length - 1 ? (
                    <>
                      Complete
                      <Check className="h-4 w-4 ml-1" />
                    </>
                  ) : (
                    <>
                      Next
                      <ChevronRight className="h-4 w-4 ml-1" />
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
